import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState(() => localStorage.getItem("hs_tenant_id") || null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("hs_token");
    if (!token) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
      if (!tenantId && data.tenants?.length) {
        const first = data.tenants[0].id;
        localStorage.setItem("hs_tenant_id", first);
        setTenantId(first);
      }
    } catch (e) {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password, totpCode) => {
    const { data } = await api.post("/auth/login", {
      email,
      password,
      ...(totpCode ? { totp_code: totpCode } : {}),
    });
    localStorage.setItem("hs_token", data.token);
    if (data.tenants?.[0]?.id) {
      localStorage.setItem("hs_tenant_id", data.tenants[0].id);
      setTenantId(data.tenants[0].id);
    } else {
      localStorage.removeItem("hs_tenant_id");
      setTenantId(null);
    }
    await refresh();
    return data;
  }, [refresh]);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    localStorage.setItem("hs_token", data.token);
    localStorage.setItem("hs_tenant_id", data.tenants[0].id);
    setTenantId(data.tenants[0].id);
    await refresh();
    return data;
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("hs_token");
    localStorage.removeItem("hs_tenant_id");
    setMe(null);
    setTenantId(null);
    window.location.href = "/login";
  }, []);

  const switchTenant = useCallback(async (id) => {
    localStorage.setItem("hs_tenant_id", id);
    setTenantId(id);
    await refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    me, loading, tenantId,
    login, signup, logout, refresh, switchTenant,
    user: me?.user,
    tenants: me?.tenants || [],
    currentTenant: me?.tenants?.find(t => t.id === tenantId) || null,
    role: me?.current_role,
    plan: me?.current_plan,
    isSuperAdmin: !!me?.user?.is_super_admin,
  }), [me, loading, tenantId, login, signup, logout, refresh, switchTenant]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

const CAN = {
  "post.write": ["admin", "editor", "super_admin"],
  "post.approve": ["admin", "approver", "super_admin"],
  "post.schedule": ["admin", "approver", "editor", "super_admin"],
  "post.delete": ["admin", "super_admin"],
  "media.write": ["admin", "editor", "super_admin"],
  "social.write": ["admin", "super_admin"],
  "tenant.settings": ["admin", "super_admin"],
  "tenant.invite": ["admin", "super_admin"],
};

export function can(role, perm) {
  if (!role) return false;
  if (role === "super_admin") return true;
  return (CAN[perm] || []).includes(role);
}
